/**
 * Input readers shared by the descriptive-statistics widgets.
 *
 * Numbers go through `@/lib/parse-number`, which reads a decimal comma where the
 * reader's language writes one. These add the two shapes that module does not cover:
 * paired data (an x and a y per line) and the ways people write a probability —
 * `0.25`, `25%` or `1/4`.
 */

import { parseNumberInput, parseNumberList } from "@/lib/parse-number";

/** Line splitter for pasted rows. */
export const LINE_BREAK = /\r?\n/;
const PERCENT_SUFFIX = /%\s*$/;
const CURRENCY = /[$€£¥]/g;
/** A semicolon frees the comma to be a decimal mark — the `parseNumberList` rule. */
const SEMICOLON_TOKENS = /[\s;]+/;
const COMMA_TOKENS = /[\s,;]+/;

export interface PairedData {
  xs: number[];
  ys: number[];
}

export interface PairLines extends PairedData {
  /** Non-blank lines that did not hold two numbers (a header row, a typo). */
  skipped: number;
}

/**
 * One `x, y` pair per line. A line is read with `parseNumberList`, so `2,5; 3,5` is a
 * decimal-comma pair; the first two numbers on a line are the pair, and a line without
 * two numbers is counted as skipped rather than silently shifting every later pair.
 */
export function parsePairLines(text: string): PairLines {
  const xs: number[] = [];
  const ys: number[] = [];
  let skipped = 0;
  for (const line of text.split(LINE_BREAK)) {
    if (line.trim() === "") {
      continue;
    }
    const numbers = parseNumberList(line);
    if (numbers.length >= 2) {
      xs.push(numbers[0]);
      ys.push(numbers[1]);
    } else {
      skipped += 1;
    }
  }
  return { xs, ys, skipped };
}

/**
 * A number, a fraction (`1/6`) or a percentage (`25%`, read as 0.25).
 *
 * Null for anything else, including a zero denominator — never NaN, so a widget that
 * forgets to check gets a type error rather than "NaN" on the page.
 */
export function parseFlexibleNumber(raw: string): number | null {
  const token = raw.replace(CURRENCY, "").trim();
  if (token === "") {
    return null;
  }
  if (PERCENT_SUFFIX.test(token)) {
    const value = parseNumberInput(token.replace(PERCENT_SUFFIX, ""));
    return value === null ? null : value / 100;
  }
  const slash = token.indexOf("/");
  if (slash !== -1) {
    const numerator = parseNumberInput(token.slice(0, slash));
    const denominator = parseNumberInput(token.slice(slash + 1));
    if (numerator === null || denominator === null || denominator === 0) {
      return null;
    }
    return numerator / denominator;
  }
  return parseNumberInput(token);
}

/**
 * A probability typed into a field.
 *
 * `asPercent` is the field's unit: when the reader has chosen percentages, a bare `9`
 * means 9 %. An explicit `%` or a fraction always means what it says, whichever unit is
 * chosen. Returns null outside 0–1.
 */
export function parseProbability(
  raw: string,
  asPercent: boolean
): number | null {
  const token = raw.trim();
  const explicit = PERCENT_SUFFIX.test(token) || token.includes("/");
  const value = parseFlexibleNumber(token);
  if (value === null) {
    return null;
  }
  const probability = asPercent && !explicit ? value / 100 : value;
  if (probability < 0 || probability > 1) {
    return null;
  }
  return probability;
}

/** Split one line of a two-column list into tokens, honouring the semicolon rule. */
export function lineTokens(line: string): string[] {
  const separators = line.includes(";") ? SEMICOLON_TOKENS : COMMA_TOKENS;
  return line
    .split(separators)
    .map((token) => token.trim())
    .filter((token) => token !== "");
}
